import config from '../aws-config';

// Ensure API URL always ends with a trailing slash; warn loudly if not set.
const rawUrl = config.apiUrl || '';
if (!rawUrl) console.error('[api] REACT_APP_API_URL is not set — API calls will fail.');
const apiBaseUrl = rawUrl.replace(/\/?$/, '/');

async function getToken() {
  return new Promise((resolve) => {
    const { CognitoUserPool } = require('amazon-cognito-identity-js');
    const pool = new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.userPoolClientId,
    });
    const user = pool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err, session) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

async function apiFetch(path, options = {}) {
  if (!rawUrl) throw new Error('API base URL is not configured. Set REACT_APP_API_URL.');

  const token = await getToken();
  if (!token) {
    // No valid Cognito session — send user to login instead of firing a doomed request.
    window.location.replace('/login');
    throw new Error('Session expired. Please log in again.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const res = await fetch(`${apiBaseUrl}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        ...(options.headers || {}),
      },
    });
    clearTimeout(timeoutId);

    const data = await res.json();

    if (res.status === 401) {
      // Token accepted locally but rejected by API Gateway — force re-login.
      window.location.replace('/login');
      throw new Error('Session expired. Please log in again.');
    }

    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') throw new Error('Request timed out. Please check your connection and try again.');
    throw err;
  }
}

// Auth (no token needed)
export const validateInviteCode = (code) =>
  fetch(`${apiBaseUrl}auth/validate-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  }).then(r => r.json());

export const registerUser = (data) =>
  fetch(`${apiBaseUrl}auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(r => r.json());

// Notes
export const getNotes = () => apiFetch('notes');
export const getNote = (id) => apiFetch(`notes/${id}`);
export const saveNote = (data) => apiFetch('notes', { method: 'POST', body: JSON.stringify(data) });
export const updateNote = (id, data) => apiFetch(`notes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteNote = (id) => apiFetch(`notes/${id}`, { method: 'DELETE' });

// Admin
export const getCaregivers = () => apiFetch('admin/caregivers');
export const generateInviteCodes = (data) => apiFetch('admin/invite', { method: 'POST', body: JSON.stringify(data) });
export const getInviteCodes = () => apiFetch('admin/invite-codes');
export const deleteInviteCode = (code) => apiFetch(`admin/invite-codes/${encodeURIComponent(code)}`, { method: 'DELETE' });

// Clients
export const getClients = () => apiFetch('clients');
export const getAdminClients = () => apiFetch('admin/clients');
export const createClient = (data) => apiFetch('admin/clients', { method: 'POST', body: JSON.stringify(data) });
export const updateClient = (id, data) => apiFetch(`admin/clients/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteClient = (id) => apiFetch(`admin/clients/${encodeURIComponent(id)}`, { method: 'DELETE' });

// Assessments + Agent
export const getAssessment = (clientId) => apiFetch(`agent/assessments/${encodeURIComponent(clientId)}`);
export const saveAssessment = (clientId, data) => apiFetch(`agent/assessments/${encodeURIComponent(clientId)}`, { method: 'PUT', body: JSON.stringify(data) });
export const agentGenerateNote = (data) => apiFetch('agent/generate', { method: 'POST', body: JSON.stringify(data) });
export const agentBatchGenerateNotes = (data) => apiFetch('agent/batch-generate', { method: 'POST', body: JSON.stringify(data) });
export const getBatchStatus = (batchId) => apiFetch(`agent/batch/${encodeURIComponent(batchId)}`);
export const parseAssessmentFile = (fileData, mimeType) => apiFetch('agent/parse-assessment', { method: 'POST', body: JSON.stringify({ fileData, mimeType }) });
export const parseEvvFile = (fileData, fileName, mimeType) => apiFetch('agent/parse-evv', { method: 'POST', body: JSON.stringify({ fileData, fileName, mimeType }) });
export const parseStoredEvvReport = (storedClientId, storedReportId) => apiFetch('agent/parse-evv', { method: 'POST', body: JSON.stringify({ storedClientId, storedReportId }) });
export const agentEvvGenerateNote = (data) => apiFetch('agent/evv-generate', { method: 'POST', body: JSON.stringify(data) });
export const listTaReports = (clientId) => apiFetch(`agent/ta-reports/${encodeURIComponent(clientId)}`);
export const uploadTaReport = (clientId, data) => apiFetch(`agent/ta-reports/${encodeURIComponent(clientId)}`, { method: 'POST', body: JSON.stringify(data) });
export const deleteTaReport = (clientId, reportId) => apiFetch(`agent/ta-reports/${encodeURIComponent(clientId)}/${encodeURIComponent(reportId)}`, { method: 'DELETE' });

// Auto-schedule (admin only)
export const getSchedule = () => apiFetch('agent/schedule');
export const upsertSchedule = (data) => apiFetch('agent/schedule', { method: 'POST', body: JSON.stringify(data) });
export const deleteSchedule = (clientId) => apiFetch(`agent/schedule/${encodeURIComponent(clientId)}`, { method: 'DELETE' });
export const runScheduleNow = () => apiFetch('agent/schedule-run', { method: 'POST' });

// SMS reminder settings (admin only)
export const getAllSmsSettings = () => apiFetch('admin/sms-settings');
export const getSmsSettings = (userId) => apiFetch(`admin/sms-settings/${userId}`);
export const saveSmsSettings = (userId, data) =>
  apiFetch(`admin/sms-settings/${userId}`, { method: 'PUT', body: JSON.stringify(data) });
export const sendTestSms = (userId) =>
  apiFetch(`admin/sms-settings/${userId}/test`, { method: 'POST' });
export const getSmsLogs = (userId) => apiFetch(`admin/sms-logs/${userId}`);
export const sendAdHocSms = (userId) =>
  apiFetch(`admin/sms-settings/${userId}/send`, { method: 'POST' });